import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { get } from "@/lib/api";
import { journeyById } from "@/data/canvas";
import { JourneyShell } from "@/components/JourneyShell";
import { MillGuide } from "@/components/MillGuide";
import { ContentCard } from "@/components/ContentCard";
import { LayerTag } from "@/components/LayerTag";
import { ShieldCheck, Building2 } from "lucide-react";

const journey = journeyById("community_guide");

export default function CommunityGuideJourney() {
  const [data, setData] = useState(null);

  useEffect(() => { get("/mill/content").then(setData).catch(() => {}); }, []);

  const bySection = (sid) => (data?.content || []).filter((c) => c.section === sid);

  return (
    <JourneyShell journey={journey}>
      <div className="space-y-10" data-testid="community-guide">
        {/* Ask panel */}
        <MillGuide />

        {/* Four sections */}
        {data && (
          <div className="space-y-8" data-testid="content-sections">
            {data.sections.map((s) => {
              const Icon = Icons[s.icon] || Icons.Folder;
              const items = bySection(s.id);
              if (!items.length) return null;
              return (
                <div key={s.id} data-testid={`section-${s.id}`}>
                  <div className="mb-3 flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#E8F0EA] text-mill-moss"><Icon className="h-4 w-4" /></span>
                    <h3 className="font-display text-lg font-semibold text-mill-ink">{s.title}</h3>
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {items.map((c) => <ContentCard key={c.id} item={c} />)}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CAROPS mapping */}
        {data?.carops && (
          <div className="rounded-2xl border border-stone-200 bg-white p-6" data-testid="carops-mapping">
            <div className="mb-4 flex items-center gap-2">
              <h3 className="font-display text-lg font-semibold text-mill-ink">CAROPS mapping</h3>
              <LayerTag layer="governed" />
            </div>
            <div className="overflow-hidden rounded-xl border border-stone-200">
              {data.carops.map((row, i) => (
                <div key={row.layer} className={`grid grid-cols-1 gap-1 px-4 py-3 sm:grid-cols-[160px_1fr] ${i % 2 ? "bg-mill-alt/40" : "bg-white"}`} data-testid={`carops-row-${row.layer.toLowerCase().replace(/\s/g, "-")}`}>
                  <span className="font-display text-sm font-semibold text-mill-ink">{row.layer}</span>
                  <span className="text-sm text-mill-muted">{row.treatment}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Publisher identity + future governed explainer */}
        {data?.publisher && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-stone-200 bg-white p-6" data-testid="publisher-identity">
              <div className="flex items-center gap-2"><Building2 className="h-5 w-5 text-mill-moss" /><h3 className="font-display text-base font-semibold text-mill-ink">Organisational publisher identity</h3></div>
              <p className="mt-2 font-mono text-sm text-mill-ink">{data.publisher.display}</p>
              <p className="mt-2 text-sm leading-relaxed text-mill-muted">{data.publisher.note}</p>
            </div>
            <div className="rounded-2xl border border-dashed border-katlas-receipt/60 bg-[#FFFDF5] p-6" data-testid="guide-future-governed">
              <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#B45309]" /><h3 className="font-display text-base font-semibold text-mill-ink">{journey.governed.title}</h3></div>
              <p className="mt-2 text-sm leading-relaxed text-mill-muted">{journey.governed.statement}</p>
              <p className="mt-3 rounded-lg bg-white px-3 py-2 text-xs text-mill-muted">
                Not connected in v0.2 — this remains a future option, subject to discovery, consent, governance and demonstrated value.
                No live KATLAS integration, real wallets or receipts are created here.
              </p>
            </div>
          </div>
        )}
      </div>
    </JourneyShell>
  );
}
